import type { ThemeName } from '@/themes/theme-config';
import type { ColorPalette } from '@/types/widget';
import { formatDDMMYYYY, pad2 } from '@/lib/date-format';

/**
 * High-contrast axis text colors per theme. Ensures readability in dark mode.
 */
const AXIS_TEXT_COLOR: Record<ThemeName, string> = {
  light: '#334155',
  dark: '#e2e8f0',
  minimal: '#3f3f46',
  colorful: '#312e81',
};

/** Return axis label color for the active theme. */
export function axisLabelColor(theme: ThemeName): string {
  return AXIS_TEXT_COLOR[theme];
}

/** Default axis font size when the widget hasn't overridden it. */
export const DEFAULT_AXIS_FONT_SIZE = 11;

/**
 * Locale used to format numeric axis ticks, bar labels, tooltip values
 * and pie segment values. Matches the data preview / table convention
 * (`val.toLocaleString()`) and is locked to tr-TR to keep grouping
 * separators consistent across the UI regardless of browser locale.
 */
export const NUMBER_LOCALE = 'tr-TR';

/**
 * Format a number for display on charts. Falls back to `String(value)`
 * when the input isn't a finite number so non-numeric tick values (rare)
 * stay readable.
 */
export function formatNumber(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toLocaleString(NUMBER_LOCALE);
  }
  if (typeof value === 'bigint') return value.toLocaleString(NUMBER_LOCALE);
  return String(value ?? '');
}

/**
 * Format a category value for display on chart axes.
 * Handles ISO date strings, Date objects, epoch numbers, and bigint epochs.
 */
export function formatAxisLabel(value: unknown): string {
  if (value == null) return '—';

  // ISO date string: YYYY-MM-DD (with optional time portion)
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return value;
  }

  // Date object (DuckDB DATE/TIMESTAMP via Arrow)
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return String(value);
    return formatDDMMYYYY(value);
  }

  // Epoch number — DuckDB TIMESTAMP can come back as a millisecond integer.
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return formatDDMMYYYY(d);
  }

  // Epoch bigint — DuckDB DATE32 can surface as bigint days since epoch
  // through Arrow on some builds. Number() loses precision past 2^53 but
  // the date range we care about is well inside safe integers.
  if (typeof value === 'bigint') {
    const d = new Date(Number(value));
    if (!Number.isNaN(d.getTime())) return formatDDMMYYYY(d);
    return value.toString();
  }

  return String(value);
}

/**
 * Format a Date / ISO date according to a bucket granularity:
 *   day   → DD.MM.YYYY
 *   month → MM.YYYY
 *   year  → YYYY
 * Returns the raw string when parsing fails.
 */
export function formatBucketedDate(
  value: unknown,
  bucket: 'day' | 'month' | 'year',
): string {
  let d: Date | null = null;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    d = new Date(value);
  }
  if (!d || Number.isNaN(d.getTime())) return formatAxisLabel(value);
  if (bucket === 'year') return String(d.getUTCFullYear());
  if (bucket === 'month') {
    return `${pad2(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
  }
  return formatDDMMYYYY(d);
}

type CategoryAxisOptions = {
  /** Explicit rotation in degrees (use `computeRotation` to derive). */
  rotation?: number | undefined;
  /** Show axis labels. Default true. */
  showLabels?: boolean;
  /** Optional axis name rendered below the tick labels. */
  name?: string | undefined;
  /** Font size for axis title only. Default DEFAULT_AXIS_FONT_SIZE. */
  titleFontSize?: number;
  /** Pre-formatted category strings (overrides built-in formatAxisLabel). */
  formattedCategories?: readonly string[];
};

/** nameGap between the X axis line and the axis title, per rotation. 
 *  Must exceed the rotated/flat tick-label extent so the title sits
 *  *past* the labels (not overlapping them). */
export function getXNameGap(rotate: number, titleFontSize: number, showLabels: boolean): number {
  if (!showLabels) return Math.floor(titleFontSize / 2) + 8;
  return rotate > 0 ? 64 + Math.floor(titleFontSize / 2) : 18 + Math.floor(titleFontSize / 2);
}

/** Left margin (grid.left) when a Y-axis title is rendered. Scales
 *  linearly with `titleFontSize` so the rotated title (whose visual
 *  width grows with font size) keeps breathing room from the widget's
 *  left border at the largest font sizes. */
export function getYAxisLeftMargin(titleFontSize: number, showLabels: boolean): number {
  return showLabels ? 48 + titleFontSize : 18 + titleFontSize;
}

/** nameGap between the Y axis line and the axis title. Grows at half
 *  the rate of `getYAxisLeftMargin`, so `grid.left − nameGap ≈ fontSize/2`
 *  — i.e. the title sits a tick-friendly distance inside the widget,
 *  outside the numeric tick-label band that `containLabel:true` places
 *  between grid.left and the axis line. */
export function getYNameGap(titleFontSize: number, showLabels: boolean): number {
  return showLabels ? 60 + Math.floor(titleFontSize / 2) : 18 + Math.floor(titleFontSize / 2);
}

/**
 * Decide X-axis label rotation: honor override when > 0, otherwise auto-rotate
 * once the category count would crowd horizontal labels.
 */
export function computeRotation(
  categoryCount: number,
  override?: number | undefined,
): number {
  if (override !== undefined && override > 0) return override;
  return categoryCount > 8 ? 45 : 0;
}

/**
 * Shared xAxis config for category charts (bar, line).
 * Provides theme-aware text color, auto-rotation, and date formatting.
 */
export function categoryXAxis(
  theme: ThemeName,
  categories: readonly unknown[],
  opts?: CategoryAxisOptions,
): Record<string, unknown> {
  const color = axisLabelColor(theme);
  const showLabels = opts?.showLabels !== false;
  const rotate = computeRotation(categories.length, opts?.rotation);
  const name = opts?.name?.trim() ?? '';
  const titleFontSize = opts?.titleFontSize ?? DEFAULT_AXIS_FONT_SIZE;
  const data = opts?.formattedCategories ?? categories.map(formatAxisLabel);

  return {
    type: 'category',
    data,
    name: name || undefined,
    // Back to the traditional centered-below-axis position, nudged
    // slightly left of centre via right-padding on the text box.
    nameLocation: 'middle',
    // Push the axis title below rotated tick labels so they never overlap.
    nameGap: getXNameGap(rotate, titleFontSize, showLabels),
    nameTextStyle: {
      color,
      fontSize: titleFontSize,
      fontWeight: 500,
    },
    axisLabel: {
      show: showLabels,
      color,
      rotate,
      interval: 0,
      fontSize: DEFAULT_AXIS_FONT_SIZE,
      overflow: 'truncate',
      width: 80,
      margin: 4,
    },
    axisLine: { lineStyle: { color } },
    axisTick: { lineStyle: { color } },
  };
}

type ValueAxisOptions = {
  showLabels?: boolean;
  name?: string | undefined;
  /** Font size for axis title only. Default DEFAULT_AXIS_FONT_SIZE. */
  titleFontSize?: number;
};

/** Shared yAxis config with theme-aware text color. */
export function valueYAxis(
  theme: ThemeName,
  opts?: ValueAxisOptions,
): Record<string, unknown> {
  const color = axisLabelColor(theme);
  const showLabels = opts?.showLabels !== false;
  const name = opts?.name?.trim() ?? '';
  const titleFontSize = opts?.titleFontSize ?? DEFAULT_AXIS_FONT_SIZE;
  return {
    type: 'value',
    name: name || undefined,
    // Y title stays vertically centred + rotated 90°, but Y_NAME_GAP is
    // sized so it hugs the widget's left border (paired with grid.left).
    nameLocation: 'middle',
    nameGap: getYNameGap(titleFontSize, showLabels),
    nameRotate: 90,
    nameTextStyle: { color, fontSize: titleFontSize, fontWeight: 500 },
    axisLabel: {
      show: showLabels,
      color,
      fontSize: DEFAULT_AXIS_FONT_SIZE,
      formatter: formatNumber,
      margin: 4,
    },
    axisLine: { lineStyle: { color } },
    splitLine: { lineStyle: { color, opacity: 0.15 } },
  };
}

// ---------------------------------------------------------------------------
// Color palettes
// ---------------------------------------------------------------------------

const PALETTES: Record<ColorPalette, readonly string[]> = {
  // ECharts' built-in default palette, pinned for determinism, extended for high-cardinality pies.
  default: [
    '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
    '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc',
    '#3d5cc6', '#a1e67e', '#ffbb33', '#f24b4b', '#5ac4eb',
    '#2da66f', '#fa662a', '#a65ec7', '#fc6ad6',
  ],
  vibrant: [
    '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#a855f7',
    '#dc2626', '#d97706', '#059669', '#2563eb', '#7c3aed',
    '#db2777', '#0d9488', '#ea580c', '#9333ea',
  ],
  pastel: [
    '#fca5a5', '#fcd34d', '#86efac', '#93c5fd', '#c4b5fd',
    '#f9a8d4', '#5eead4', '#fdba74', '#d8b4fe',
    '#f87171', '#fbbf24', '#4ade80', '#60a5fa', '#a78bfa',
    '#f472b6', '#2dd4bf', '#fb923c', '#c084fc',
  ],
  cool: [
    '#0ea5e9', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
    '#22d3ee', '#60a5fa', '#a78bfa', '#818cf8',
    '#0284c7', '#0891b2', '#2563eb', '#4f46e5', '#7c3aed',
    '#0ea5e9', '#3b82f6', '#8b5cf6', '#6366f1',
  ],
  warm: [
    '#dc2626', '#ea580c', '#f59e0b', '#eab308', '#d97706',
    '#f97316', '#fb923c', '#facc15', '#fbbf24',
    '#991b1b', '#9a3412', '#b45309', '#a16207', '#78350f',
    '#c2410c', '#ea580c', '#ca8a04', '#d97706',
  ],
  mono: [
    '#1e293b', '#334155', '#475569', '#64748b', '#94a3b8',
    '#cbd5e1', '#e2e8f0', '#475569', '#334155',
    '#0f172a', '#1e293b', '#334155', '#475569', '#64748b',
    '#94a3b8', '#f1f5f9', '#1e293b', '#0f172a',
  ],
};

/** Return the color array for a palette name (defaults to `default`). */
export function paletteColors(name: ColorPalette | undefined): readonly string[] {
  return PALETTES[name ?? 'default'];
}
